import React, { useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { CalendarCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin-only one-shot button that runs the attendance auto-mark-absent
 * backfill across a chosen date range. Useful when the nightly cron missed a
 * day or after a fresh data import. Idempotent on the backend.
 */
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export const AttendanceGapSyncCard = () => {
  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(daysAgoISO(1));   // never auto-mark today
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const run = async () => {
    if (!from || !to) { toast.error('Pick a from and to date'); return; }
    if (from > to) { toast.error('"From" must be on/before "To"'); return; }
    if (to >= todayISO()) { toast.error('"To" must be yesterday or earlier — today is still open'); return; }
    setRunning(true);
    try {
      const res = await api.post(`/attendance/auto-mark-absent/backfill?date_from=${from}&date_to=${to}`);
      if (res.data.success) {
        const totals = res.data.data.totals;
        setLastResult(totals);
        toast.success(`Synced ${totals.days_processed} day(s) — ${totals.marked} new absent row(s) added`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally { setRunning(false); }
  };

  return (
    <Card data-testid="attendance-gap-sync-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <CalendarCheck className="h-5 w-5 text-amber-500" />Sync Attendance Gaps
          {lastResult && (
            <Badge variant="outline" className="text-[10px] ml-2" data-testid="gap-sync-last-result">
              last: {lastResult.marked} added · {lastResult.skipped} skipped
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Fills any missing past work-days with auto-generated <code>absent</code> rows for all active employees.
          Honours each employee&apos;s weekly off, joining date, company holidays, and approved leaves.
          Use this when the nightly cron missed a day. Safe to run any time — idempotent.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gap-sync-from">From</Label>
            <Input id="gap-sync-from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} disabled={running} data-testid="gap-sync-from" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gap-sync-to">To</Label>
            <Input id="gap-sync-to" type="date" value={to} min={from} max={daysAgoISO(1)} onChange={(e) => setTo(e.target.value)} disabled={running} data-testid="gap-sync-to" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Defaults to the last 30 days, ending yesterday.</p>
          <Button onClick={run} disabled={running} data-testid="gap-sync-run-btn">
            {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running…</> : 'Run Sync'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
